import {
    FeatureToggle,
    FeatureToggleProperties,
    FeatureTogglesProvider as FeatureTogglesProviderClient,
} from '@40seas/entities/feature-toggles';
import { SetupDashboard, SetupModal } from '@40seas/frontend/libs/entities';
import { datadogRumProvider } from '@40seas/frontend/libs/providers';
import { FeatureTogglesService } from '@40seas/frontend/libs/services';
import { LDClient as ILDClient } from 'launchdarkly-js-client-sdk';
import * as React from 'react';
import { useCallback, useEffect } from 'react';

export type IFeatureToggleClient = {
    conduct: (key: string, defaultValue?: boolean) => any;
    identify: (context: Partial<FeatureToggleProperties>) => void;
    overrideFeatureToggleValues: { [key: string]: any };
    featureToggleProperties: Partial<FeatureToggleProperties>;
    isReady: boolean;
};

export function useFeatureToggleClient(): IFeatureToggleClient {
    const context = React.useContext(FeatureToggleContext);

    return context;
}

export const FeatureToggleContext = React.createContext<IFeatureToggleClient>({
    conduct: (key: string) => null,
    identify: (context: any) => null,
    overrideFeatureToggleValues: {},
    featureToggleProperties: {},
    isReady: false,
});

export const FeatureToggleProvider = ({ setup, children }: { setup: SetupDashboard | SetupModal; children: React.ReactNode }) => {
    // we disable launch darkly for now. we will enable it in the future
    const disableLaunchDarkly = true;
    const isLocalhost = typeof window !== 'undefined' && window?.location?.hostname === 'localhost';
    const [client, setClient] = React.useState<ILDClient | undefined>(undefined);
    const [isReady, setIsReady] = React.useState<boolean>(false);
    const [featureToggles, setFeatureToggles] = React.useState<FeatureToggle[]>([]);
    const [featureToggleProperties, setFeatureToggleProperties] = React.useState<Partial<FeatureToggleProperties>>({});

    useEffect(() => {
        // Check if setup is defined
        if (setup) {
            const fetchFeatureToggleProperties = async () => {
                try {
                    const apiUrl = setup.urls.API_URL.endsWith('/') ? `${setup.urls.API_URL}v1` : `${setup.urls.API_URL}/v1`;
                    const featureTogglesService = new FeatureTogglesService(apiUrl, {});
                    const featureTogglesResponse = await featureTogglesService.getFeatureToggles();
                    setFeatureToggles(featureTogglesResponse);
                    setIsReady(true);
                } catch (error) {
                    datadogRumProvider.reportToDatadog(error);
                }
            };

            // Call the fetch function only once
            fetchFeatureToggleProperties();
        }
    }, [setup]);

    // if(isLocalhost || disableLaunchDarkly) {
    //     return <>{children}</>;
    // }

    // if (!client) {
    //     const _client = LDClient.initialize(
    //         '64c005721ea6461330e89d59',
    //         {
    //             key: '40Seas',
    //         },
    //         {
    //             inspectors: [
    //                 {
    //                     type: 'flag-used',
    //                     name: 'dd-inspector',
    //                     method: (key: string, detail: LDClient.LDEvaluationDetail) => {
    //                         datadogRumProvider.addFeatureFlagEvaluation(key, detail.value);
    //                     },
    //                 },
    //             ],
    //         },
    //     );
    //     setClient(_client);
    //     _client.on('ready', () => setIsReady(true));
    //     _client.on('error', (e) => {
    //         console.warn('LaunchDarkly error', e);
    //     });
    // }

    const getValue = useCallback(
        (key: string, defaultValue?: boolean) => {
            const featureToggle = featureToggles.find((toggle) => toggle.name === key);
            return featureToggle ? FeatureTogglesProviderClient.getValue(featureToggle, featureToggleProperties) : defaultValue;
        },
        [featureToggles, featureToggleProperties],
    );

    const overrideFeatureToggleValues = getOverrideFeatureToggleValues();

    const conduct = (key: string, defaultValue?: boolean) => {
        if (overrideFeatureToggleValues[key] !== undefined) {
            return overrideFeatureToggleValues[key];
        }

        if (!client) {
            return getValue(key, defaultValue);
        }

        return client.variation(key, defaultValue);
    };

    return (
        <FeatureToggleContext.Provider
            value={{
                identify: client ? client?.identify.bind(client) : setFeatureToggleProperties,
                conduct,
                overrideFeatureToggleValues,
                featureToggleProperties,
                isReady,
            }}
        >
            {children}
        </FeatureToggleContext.Provider>
    );
};

function getOverrideFeatureToggleValues(): { [key: string]: any } {
    const overrideFeatureToggleValues: { [key: string]: any } = {};

    new URLSearchParams(window.location.search).forEach((value, key) => {
        if (key.startsWith('_ft_')) {
            try {
                overrideFeatureToggleValues[key.replace('_ft_', '')] = JSON.parse(value);
            } catch (e) {
                overrideFeatureToggleValues[key.replace('_ft_', '')] = value;
            }
        }
    });

    return overrideFeatureToggleValues;
}
